import { Email, Lock, Visibility, VisibilityOff } from "@mui/icons-material";
import { CircularProgress, Fade, Grow, Snackbar } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../../config/apiBase";
import { storeAdminSession } from "../../../utils/adminSession";

const theme = createTheme({
  palette: {
    primary: {
      main: "rgb(29, 161, 242)",
    },
  },
  typography: {
    fontFamily: "'Poppins', 'Roboto', 'Arial', sans-serif",
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          fontWeight: 600,
          boxShadow: "0 4px 10px rgba(29, 161, 242, 0.3)",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 8,
          },
        },
      },
    },
  },
});

export default function SignInSide() {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const formData = {
      email: data.get("email"),
      password: data.get("password"),
    };

    setError("");
    setEmailError("");
    setPasswordError("");

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.email) {
      setEmailError("Email is required");
    } else if (!emailRegex.test(formData.email)) {
      setEmailError("Email is not a valid format");
    }

    if (!formData.password) {
      setPasswordError("Password is required");
    }

    if (
      formData.email &&
      emailRegex.test(formData.email) &&
      formData.password
    ) {
      setIsLoading(true);
      try {
        const response = await fetch(
          `${API_BASE_URL}/admin/login`,
          {
            method: "POST",
            mode: "cors",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(formData),
          }
        );

        const responseData = await response.json();

        if (response.ok && responseData.response_code === 200 && responseData.result.status) {
          // Extract token from the correct response structure
          const token = responseData.result.token;
          storeAdminSession(token);

          setSnackbarMessage(responseData.result.message || "Login successful!");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);

          setTimeout(() => {
            navigate("/dd");
          }, 2000);
        } else {
          // Handle error response
          setError(responseData.result?.message || "Login failed");
        }
      } catch (error) {
        console.error("Error during login:", error.message);
        setError("An error occurred during login");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleChange = () => {
    setEmailError("");
    setPasswordError("");
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          padding: { xs: 2, sm: 4, md: 6 },
          position: 'relative',
          overflow: 'hidden',
          // Enhanced background with gradient
          background: 'linear-gradient(135deg, #f0f5fa 0%, #e0eafc 100%)',
        }}
      >
        {/* Background decorative elements */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 0,
            overflow: 'hidden',
          }}
        >
          {/* Large circle */}
          <Box
            sx={{
              position: 'absolute',
              width: '500px',
              height: '500px',
              borderRadius: '50%',
              background: 'linear-gradient(45deg, rgba(29, 161, 242, 0.1), rgba(0, 0, 255, 0.1))',
              top: '-100px',
              right: '-100px',
            }}
          />
          
          {/* Small circles */}
          <Box
            sx={{
              position: 'absolute',
              width: '200px',
              height: '200px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(0, 0, 255, 0.08), rgba(29, 161, 242, 0.08))',
              bottom: '10%',
              left: '5%',
            }}
          />
          
          <Box
            sx={{
              position: 'absolute',
              width: '300px',
              height: '300px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(29, 161, 242, 0.05), rgba(0, 0, 255, 0.05))',
              top: '10%',
              left: '15%',
            }}
          />
          
          {/* Wave shape */}
          <Box
            sx={{
              position: 'absolute',
              width: '100%',
              height: '800px',
              background: 'radial-gradient(ellipse at 50% 120%, rgba(29, 161, 242, 0.1) 0%, rgba(255, 255, 255, 0) 70%)',
              bottom: '-400px',
              left: 0,
              transform: 'skewY(-3deg)',
            }}
          />
          
          {/* Floating rectangles */}
          <Box
            sx={{
              position: 'absolute',
              width: '80px',
              height: '80px',
              background: 'linear-gradient(135deg, rgba(29, 161, 242, 0.2), rgba(0, 0, 255, 0.2))',
              borderRadius: '12px',
              transform: 'rotate(-15deg)',
              bottom: '20%',
              right: '15%',
            }}
          />
          
          <Box
            sx={{
              position: 'absolute',
              width: '40px',
              height: '40px',
              background: 'linear-gradient(135deg, rgba(0, 0, 255, 0.15), rgba(29, 161, 242, 0.15))',
              borderRadius: '8px',
              transform: 'rotate(30deg)',
              top: '25%',
              left: '30%',
            }}
          />
        </Box>

        <Paper
          elevation={12}
          sx={{
            width: '100%',
            maxWidth: '1200px',
            borderRadius: '16px',
            overflow: 'hidden',
            position: 'relative',
            zIndex: 1,
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Grid container component="main">
            <CssBaseline />
            
            {/* Left Side - Branding & Enhanced Gradient Background */}
            <Grid
              item
              xs={12}
              sm={12}
              md={7}
              sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                color: 'white',
                padding: { xs: 4, sm: 6, md: 8 },
                minHeight: { xs: '300px', md: '600px' },
                overflow: 'hidden',
              }}
            >
              {/* Enhanced background gradient with overlay */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(135deg, #0047AB, #1DA1F2, #22a3f1)',
                  zIndex: -1,
                }}
              />
              
              {/* Decorative shapes for left side */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 0,
                  overflow: 'hidden',
                }}
              >
                {/* Circles */}
                <Box
                  sx={{
                    position: 'absolute',
                    width: '400px',
                    height: '400px',
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 70%)',
                    top: '-150px',
                    left: '-150px',
                  }}
                />
                
                <Box
                  sx={{
                    position: 'absolute',
                    width: '300px',
                    height: '300px',
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
                    bottom: '-50px',
                    right: '-100px',
                  }}
                />
                
                {/* Floating shapes */}
                <Box
                  sx={{
                    position: 'absolute',
                    width: '80px',
                    height: '80px',
                    borderRadius: '20px',
                    background: 'rgba(255,255,255,0.1)',
                    bottom: '15%',
                    left: '10%',
                    transform: 'rotate(15deg)',
                  }}
                />
                
                <Box
                  sx={{
                    position: 'absolute',
                    width: '60px',
                    height: '60px',
                    borderRadius: '15px',
                    background: 'rgba(255,255,255,0.08)',
                    top: '20%',
                    right: '15%',
                    transform: 'rotate(-20deg)',
                  }}
                />
                
                <Box
                  sx={{
                    position: 'absolute',
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    background: 'rgba(255,255,255,0.12)',
                    top: '40%',
                    left: '20%',
                    transform: 'rotate(45deg)',
                  }}
                />
                
                {/* Wave effect at bottom */}
                <Box
                  sx={{
                    position: 'absolute',
                    height: '200px',
                    width: '150%',
                    bottom: '-120px',
                    left: '-25%',
                    background: 'rgba(255,255,255,0.08)',
                    borderRadius: '50%',
                    transform: 'rotate(-5deg)',
                  }}
                />
              </Box>

              {/* Content */}
              <Box 
                sx={{ 
                  position: 'relative', 
                  zIndex: 2, 
                  maxWidth: '80%',
                  textAlign: 'center'
                }}
              >
                <img
                  src="/assets/image.png"
                  alt="Oi-Jo Logo"
                  style={{ 
                    width: '60%', 
                    maxWidth: '300px', 
                    marginBottom: '2rem',
                    filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.2))'
                  }}
                />
                <Typography 
                  variant="h3" 
                  component="h1" 
                  sx={{ 
                    fontWeight: 700, 
                    marginBottom: 3,
                    textShadow: '0 2px 10px rgba(0,0,0,0.3)'
                  }}
                >
                  Oi-Jo Admin Dashboard
                </Typography>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    opacity: 0.9,
                    maxWidth: '600px', 
                    marginX: 'auto',
                    textShadow: '0 2px 6px rgba(0,0,0,0.3)'
                  }}
                >
                  Manage your platform with our powerful and intuitive admin tools
                </Typography>
              </Box>
              
              {/* Footer */}
              <Box 
                sx={{ 
                  position: 'absolute', 
                  bottom: 16, 
                  left: 0, 
                  right: 0, 
                  textAlign: 'center',
                  zIndex: 2
                }}
              >
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                  © 2025 Oi-Jo. All rights reserved.
                </Typography>
              </Box>
            </Grid>
            
            {/* Right Side - Login Form */}
            <Grid 
              item 
              xs={12} 
              sm={12} 
              md={5} 
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                backgroundColor: '#f9fbfd',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {/* Subtle background pattern for right side */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  opacity: 0.03,
                  backgroundImage: 'radial-gradient(#1DA1F2 1px, transparent 1px), radial-gradient(#1DA1F2 1px, transparent 1px)',
                  backgroundSize: '20px 20px',
                  backgroundPosition: '0 0, 10px 10px',
                }}
              />

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: { xs: 3, sm: 4, md: 6 },
                  height: '100%',
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                <Box 
                  sx={{ 
                    display: { xs: 'block', md: 'none' }, 
                    mb: 4, 
                    textAlign: 'center' 
                  }}
                >
                  <img
                    src="/assets/image.png"
                    alt="Oi-Jo Logo"
                    style={{ width: '40%', maxWidth: '180px' }}
                  />
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      mt: 2, 
                      color: 'primary.main',
                      fontWeight: 600
                    }}
                  >
                    Oi-Jo Admin
                  </Typography>
                </Box>

                <Typography 
                  component="h1" 
                  variant="h4" 
                  sx={{ 
                    mb: 4, 
                    fontWeight: 700,
                    color: '#333',
                    background: 'linear-gradient(90deg, #0047AB, #1DA1F2)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Sign in
                </Typography>
                
                <Box
                  component="form"
                  noValidate
                  onSubmit={handleSubmit}
                  sx={{ 
                    width: '100%',
                    maxWidth: '360px'
                  }}
                >
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="email"
                    name="email"
                    autoComplete="email"
                    placeholder="Email Address"
                    autoFocus
                    error={!!emailError}
                    helperText={emailError}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email color="primary" />
                        </InputAdornment>
                      ),
                      sx: {
                        height: "50px",
                      },
                    }}
                    onChange={handleChange}
                    variant="outlined"
                  />

                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    name="password"
                    placeholder="Password"
                    type={showPassword ? "text" : "password"}
                    id="password"
                    autoComplete="current-password"
                    error={!!passwordError}
                    helperText={passwordError}
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock color="primary" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                      sx: { 
                        height: "50px" 
                      },
                    }}
                    variant="outlined"
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{
                      mt: 4,
                      mb: 2,
                      height: 50,
                      fontSize: '1rem',
                      background: 'linear-gradient(90deg, #0047AB, #1DA1F2)',
                      transition: '0.3s',
                      '&:hover': {
                        background: 'linear-gradient(90deg, #003d91, #0e93e0)',
                        boxShadow: '0 6px 15px rgba(29, 161, 242, 0.4)',
                      }
                    }}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Sign In"
                    )}
                  </Button>

                  <Fade in={!!error}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        mt: 2, 
                        color: 'error.main',
                        textAlign: 'center'
                      }}
                    >
                      {error}
                    </Typography>
                  </Fade>
                  
                  <Box sx={{ mt: 4, textAlign: 'center' }}>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                    >
                      Having trouble? <span style={{ color: theme.palette.primary.main, cursor: 'pointer' }}>Contact support</span>
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>
        
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={2000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          TransitionComponent={Grow}
        >
          <MuiAlert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </MuiAlert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
}